<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'src/services/i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import { formatSnakeCaseToTitle } from 'src/helpers/utilities';
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from 'src/helpers/interface_helpers';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from 'src/helpers/timer_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	//Stores
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Button, Toggle, Text, toast, TimeWheel } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	const id = real_id(device.id);
	const component = isPeripheralDevice(device.type)
		? device.component
		: get_gen_2_component_channel_structure(device);

	const componentIndex = isPeripheralDevice(device.type)
		? Number(device.component.split(':')[1])
		: get_device_component_index(device);

	let settingsPerChannel = readDeviceSettings(id)[component],
		subsettings = ['auto_on', 'auto_off'];

	let loading = { auto_on: false, auto_off: false };
	let min = 0,
		max = 2147483;

	let timersValues = {
		auto_on: settingsPerChannel['auto_on'],
		auto_on_delay: settingsPerChannel['auto_on_delay'],
		auto_off: settingsPerChannel['auto_off'],
		auto_off_delay: settingsPerChannel['auto_off_delay'],
	};

	let lastCustomDelays = {
		auto_on_delay: timersValues.auto_on_delay > 0 ? timersValues.auto_on_delay : 60,
		auto_off_delay: timersValues.auto_off_delay > 0 ? timersValues.auto_off_delay : 60,
	};

	let selectedHour = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMinute = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedSecond = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: errors = {
		auto_on_error:
			convertToSeconds(
				selectedHour['auto_on_delay'],
				selectedMinute['auto_on_delay'],
				selectedSecond['auto_on_delay'],
				selectedMs['auto_on_delay'],
			) > max
				? infoText
				: '',
		auto_off_error:
			convertToSeconds(
				selectedHour['auto_off_delay'],
				selectedMinute['auto_off_delay'],
				selectedSecond['auto_off_delay'],
				selectedMs['auto_off_delay'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		updateTimeValues('auto_on_delay');
		updateTimeValues('auto_off_delay');
	});

	const getTimeInSeconds = (type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(timersValues[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const set_auto = async (setting) => {
		loading[setting] = true;

		const secconds = getTimeInSeconds(`${setting}_delay` as any);
		const params = {
			id: componentIndex,
			config: {
				[setting]: timersValues[setting],
				[setting + '_delay']: secconds,
			},
		};

		const res: SettingRequest = await gen2Request(device, method, params);
		if (res.isok) {
			if (!timersValues[setting]) {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: $format(setting) },
					}),
				});
			} else {
				const toastKey = `${setting}_set_toast`;
				const formattedTime = formatTimeLabel(
					selectedHour[`${setting}_delay`],
					selectedMinute[`${setting}_delay`],
					selectedSecond[`${setting}_delay`],
					selectedMs[`${setting}_delay`],
				);
				toast.open({
					type: 'success',
					title: `${$format(toastKey)} ${formattedTime}.`,
				});
			}
		} else {
			if (res?.errors?.message) {
				toast.open({ type: 'danger', title: res?.errors?.message });
			} else {
				toast.open({ type: 'danger', title: $format('timer_err_toast') });
			}
		}
		loading[setting] = false;
	};

	const handle_toggler = async (setting) => {
		if (loading[setting]) return;

		timersValues[setting] = !timersValues[setting];

		const delayKey = `${setting}_delay`;
		if (!timersValues[setting]) {
			if (timersValues[delayKey] > 0) lastCustomDelays[delayKey] = timersValues[delayKey];
		} else {
			if (!timersValues[delayKey] || timersValues[delayKey] <= 0) {
				timersValues[delayKey] =
					lastCustomDelays[delayKey] > 0 ? lastCustomDelays[delayKey] : 60;
				updateTimeValues(delayKey as 'auto_on_delay' | 'auto_off_delay');
			}
		}

		await set_auto(setting);
	};
</script>

{#each subsettings as setting, index}
	{#if index > 0}
		<hr class="mt-l mb-l" />
	{/if}

	<Toggle
		size="m"
		checked={timersValues[setting]}
		disabled={loading[setting]}
		on:click={() => handle_toggler(setting)}
		id="toggle-timer-g2-{setting}"
		aria-label={formatSnakeCaseToTitle($format(setting))}
	>
		<Text element="span" styleType="controls-small" class="text-white">
			{formatSnakeCaseToTitle($format(setting))}
		</Text>
	</Toggle>

	<Text styleType="tiny" class="text-white" style="margin-top: 2px">
		{$format(`${setting}_toggle_info`, {
			default:
				setting === 'auto_on'
					? 'Turns the device on automatically after the selected time'
					: 'Turns the device off automatically after the selected time',
		})}
	</Text>

	{#if timersValues[setting]}
		<Text styleType="controls-small" class="mt-s-plus mb-s">
			{$format(`${setting}_time_label`, {
				default:
					setting === 'auto_on' ? 'Turn the device on after' : 'Turn the device off after',
			})}
			<Text element="span" styleType="body-small" class="text-primary text-bold">
				{formatTimeLabel(
					selectedHour[`${setting}_delay`],
					selectedMinute[`${setting}_delay`],
					selectedSecond[`${setting}_delay`],
					selectedMs[`${setting}_delay`],
				)}
			</Text>
		</Text>

		<TimeWheel
			id="timewheel-cwwd3j1-{setting}"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', {
					default: 'Time wheel input',
				}),
				buttonAriaLabel: $format('label_time_wheel_button', {
					default: 'Time wheel button',
				}),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[`${setting}_delay`]}
			bind:selectedMinute={selectedMinute[`${setting}_delay`]}
			bind:selectedSecond={selectedSecond[`${setting}_delay`]}
			bind:selectedMs={selectedMs[`${setting}_delay`]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}_error`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}_error`]}
			size="m"
			width="full"
			on:click={() => set_auto(setting)}>{$format('button_save')}</Button
		>
	{/if}
{/each}
